import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, 
  Search, 
  UserPlus, 
  UserMinus, 
  Star,
  Trophy,
  Loader2,
  X
} from 'lucide-react';
import { generateInitials } from '../lib/utils';

export default function FavoritePlayers() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await userAPI.getFavorites();
      setFavorites(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Stammspieler');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await userAPI.searchPlayers(query);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleAddFavorite = async (playerId, playerName) => {
    setAddingId(playerId);
    try {
      await userAPI.addFavorite(playerId);
      toast.success(`${playerName} zu Stammspielern hinzugefügt`);
      await fetchFavorites();
      // Update search results to reflect the change
      setSearchResults(prev => prev.map(p => 
        p.id === playerId ? { ...p, is_favorite: true } : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally {
      setAddingId(null);
    }
  };

  const handleRemoveFavorite = async (playerId, playerName) => {
    setRemovingId(playerId);
    try {
      await userAPI.removeFavorite(playerId);
      toast.success(`${playerName} aus Stammspielern entfernt`);
      setFavorites(prev => prev.filter(p => p.id !== playerId));
      // Update search results
      setSearchResults(prev => prev.map(p => 
        p.id === playerId ? { ...p, is_favorite: false } : p
      ));
    } catch (error) {
      toast.error('Fehler beim Entfernen');
    } finally {
      setRemovingId(null);
    }
  };

  const renderPlayerCard = (player, isFavorite) => (
    <Card key={player.id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-12 w-12">
            <AvatarImage src={player.avatar_url} />
            <AvatarFallback className="bg-gradient-to-br from-tunis-blue to-tunis-teal text-white">
              {generateInitials(player.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-tunis-blue truncate">{player.name}</h3>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Trophy className="w-3 h-3 text-amber-500" />
                {player.elo_points || 1000} ELO
              </span>
              {player.matches_played > 0 && (
                <span>{player.matches_played} Spiele</span>
              )}
            </div>
          </div>
          {isFavorite ? (
            <Button
              variant="outline"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => handleRemoveFavorite(player.id, player.name)}
              disabled={removingId === player.id}
            >
              {removingId === player.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserMinus className="w-4 h-4 mr-1" />
                  Entfernen
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="text-tunis-teal hover:text-tunis-teal hover:bg-tunis-teal/10"
              onClick={() => handleAddFavorite(player.id, player.name)}
              disabled={addingId === player.id}
            >
              {addingId === player.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-1" />
                  Hinzufügen
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-tunis-blue flex items-center gap-2">
            <Star className="w-6 h-6 text-amber-500" />
            Meine Stammspieler
          </h1>
          <p className="text-muted-foreground mt-1">
            Verwalte deine Lieblingsspieler und lade sie schnell zu Spielen ein
          </p>
        </div>

        {/* Search Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-tunis-teal" />
              Spieler suchen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Name oder E-Mail eingeben..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 h-12"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Search Results */}
            {searching && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-tunis-teal" />
              </div>
            )}

            {!searching && searchResults.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {searchResults.length} Spieler gefunden
                </p>
                {searchResults.map((player) => 
                  renderPlayerCard(player, player.is_favorite || favorites.some(f => f.id === player.id))
                )}
              </div>
            )}

            {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                Keine Spieler gefunden
              </p>
            )}
          </CardContent>
        </Card>

        {/* Favorites List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-tunis-teal" />
              Deine Stammspieler
              <Badge variant="secondary">{favorites.length}/20</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-600 mb-2">Keine Stammspieler</h3>
                <p className="text-sm text-muted-foreground">
                  Suche nach Spielern und füge sie zu deinen Stammspielern hinzu
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {favorites.map((player) => renderPlayerCard(player, true))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
